import { DateRange } from '@angular/material/datepicker';
import { TrendingChartType, TrendingEnumSeriesData } from '@store';

import dayjs from 'dayjs';
import * as Highcharts from 'highcharts';
import { Series } from 'highcharts';
import moment from 'moment';
import { ViguideBreakpoints } from '../../../../shared/common/ui-constants';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

const hourMs = 3600000;
const monthMs = 2678400000;
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export enum ZoomType {
  X = 'x',
  Xy = 'xy',
}
export interface TrendingSeries extends Omit<Series, 'userOptions'> {
  name: string;
  color: string;
  visible: boolean;
  userOptions: {
    deviceType: string;
    deviceId: string;
    boilerSerial: string;
    shortDeviceName: string;
    id: string;
    name: string;
  };
  eventOptions: {
    legendItemClick({ target }): void;
  };
}
export interface SeriesWithGroupedData extends Series {
  hasGroupedData: boolean;
}

export const initBaseTrendingChartOptions: Highcharts.Options = {
  chart: {
    zooming: {
      type: ZoomType.X,
    },
    height: 500,
  },
  rangeSelector: {
    enabled: false,
  },
  credits: {
    enabled: false,
  },
  xAxis: {
    type: 'datetime',
    minRange: hourMs,
    maxRange: monthMs,
    ordinal: false,
    zoomEnabled: true,
  },
  scrollbar: {
    enabled: false,
  },
  yAxis: [
    {
      title: { text: null },
      max: null,
      opposite: false,
      zoomEnabled: false,
    },
    {
      title: { text: null },
      max: 100,
      opposite: false,
      zoomEnabled: false,
    },
    {
      title: { text: null },
      max: 1000,
      zoomEnabled: false,
    },
  ],
  plotOptions: {},
  legend: {
    enabled: false,
  },
  tooltip: {
    className: 'trending-chart-tooltip',
    followPointer: true,
    shared: true,
    shape: 'square',
    backgroundColor: '#fff',
    borderColor: '#fff',
    borderRadius: 8,
    borderWidth: 0,
    useHTML: true,
    outside: true,
    formatter: function (context): string {
      return showChartTooltip(context, this, true, true);
    },
  } as any,
  time: {
    timezone: null,
  },
  navigator: {
    xAxis: {},
    series: {
      lineColor: 'transparent',
    },
  },
  series: [],
};

export const initXRangeTrendingChartOptions: Highcharts.Options = {
  chart: {
    type: TrendingChartType.XRange,
    zooming: {
      type: ZoomType.X,
    },
  },
  title: {
    text: '',
  },
  xAxis: {
    type: 'datetime',
    zoomEnabled: true,
    plotLines: [
      {
        color: '#ff000082',
        zIndex: 10,
        width: 10,
        value: Date.UTC(2024, 4, 17),
        events: null,
      },
    ],
  },
  yAxis: {
    categories: [],
  },
  rangeSelector: {
    enabled: false,
  },
  credits: {
    enabled: false,
  },
  scrollbar: {
    enabled: false,
  },
  plotOptions: {
    series: {
      visible: true,
      events: {
        legendItemClick: (e) => {
          e.preventDefault();
        },
      },
    },
  },
  legend: {
    enabled: false,
  },
  tooltip: {
    followPointer: true,
    shared: true,
    shape: 'square',
    backgroundColor: '#fff',
    borderColor: '#fff',
    borderRadius: 8,
    borderWidth: 0,
    useHTML: true,
    outside: true,
    // eslint-disable-next-line
    formatter: function (context) {
      const wrapperStyles = `
        display: flex;
        padding: 8px 16px;
        flex-direction: column;
        gap: 1px;
      `;

      const separatorStyle = `
        display: flex;
        width: 100%;
        background-color: #e7e7e7;
        height: 1px;
        margin: 6px 0;
      `;

      const transparentSeparatorStyles = `
        display: flex;
        width: 100%;
        height: 1px;
        margin: 6px 0;
      `;

      const entryTitleStyles = `
        width: 100%;
        display: flex;
        justify-content: space-between;
      `;

      const entryStyles = `
        width: 100%;
        display: flex;
        justify-content: space-between;
        margin-bottom: 2px;
      `;

      let plotLinesToRender = [];

      plotLinesToRender = context?.chart?.xAxis[0]?.plotLinesAndBands?.filter((plotLine) => {
        return !!plotLine?.options?.renderPlotLineInTooltip;
      });

      let errorsContent = plotLinesToRender?.map((plotLine) => {
        const plotLineDate = `<div style="${entryTitleStyles}"><span>${plotLine.options.date}</span></div>`;

        const dateLine = `<div style="${entryStyles}"><span>${plotLineDate}</span></div>`;
        const separator = `<div style="${separatorStyle}"> </div>`;
        const transparentSeparator = `<div style="${transparentSeparatorStyles}"> </div>`;

        let errorSection = `${dateLine}${separator}`;

        plotLine.options.errors.forEach((error) => {
          const newLine = `<div style="${entryStyles}"><span>${error}</span></div>`;
          errorSection = errorSection + newLine;
        });

        errorSection += transparentSeparator;

        return errorSection;
      });

      const trendsContent = this.points.reduce(function (acc, point: any, index) {
        const valueLine = `<div style="${entryStyles}"><span><span style="color: ${point.series.color}; margin-right: 5px">\u25A0</span>${point.series.name}</span>`;

        if (index === 0) {
          const dateText = `${moment(point.point.x).format('HH:mm DD.MM')} - ${moment(
            point.point.x2
          ).format('HH:mm DD.MM')}`;

          const dateLine = `<div style="${entryTitleStyles}"><span>${dateText}</span></div>`;
          const separator = `<div style="${separatorStyle}"></div>`;

          return `${dateLine}${separator}${valueLine}`;
        }

        return `${acc}${valueLine}`;
      }, '<b>' + this.x + '</b>');

      let wrapper = '';
      if (!!errorsContent) {
        wrapper = `<span style="${wrapperStyles};">${errorsContent.join(
          ''
        )}</span><span style="${wrapperStyles};">${trendsContent}</span>`;
      } else {
        wrapper = `<span style="${wrapperStyles};">${trendsContent}</span>`;
      }

      return wrapper;
    },
  } as any,
  time: {
    timezone: null,
  },
  navigator: {
    adaptToUpdatedData: false,
    xAxis: {},
    series: {
      lineColor: 'transparent',
    },
  },
  series: [],
};

export function getTrendingChartYAxis(chartOptions: Highcharts.Options, maxRange: number): number {
  if (maxRange) {
    const yAxis = chartOptions.yAxis as Array<Highcharts.YAxisOptions>;

    return yAxis.findIndex((axis) => axis.max === maxRange);
  }

  return 0;
}

export function resizeTrendingChart(
  chartOptions: Highcharts.Options,
  chartInstance: Highcharts.Chart
): void {
  if (!isChartReady(chartInstance)) {
    return;
  }

  const windowWidth = window.innerWidth;
  // 56 = 2 * 24px padding + 8 additional margin
  const margin = 96 + 56;
  const marginMobiles = 48 + 56;
  const stickyPanelWidth = 300;
  const yScrollWidth = 17;

  const lowerThanMediumWidth = windowWidth < ViguideBreakpoints.md;
  if (lowerThanMediumWidth) {
    chartOptions.chart.width = windowWidth - marginMobiles;
  } else {
    chartOptions.chart.width = windowWidth - margin - stickyPanelWidth - yScrollWidth;
  }

  chartInstance.update(chartOptions, true);
}

export function getTrendingChartLabelsPosition(
  labelWidth: number,
  labelHeight: number,
  point: Highcharts.TooltipPositionerPointObject,
  chartInstance: Highcharts.Chart
): { x: number; y: number } {
  const marginRight = 64;
  const marginLeft = 42;

  let x: number;
  let y = point.plotY + chartInstance.plotTop - labelHeight;

  // set initial x
  x = point.plotX + chartInstance.plotLeft;

  // flip label to left side
  x = x - labelWidth;

  // if LEFT overflow then flip label
  if (x + marginLeft < 0) {
    x = x + labelWidth;
  }

  // if RIGHT overflow, center label
  if (x + labelWidth > chartInstance.plotLeft + chartInstance.plotWidth + marginRight) {
    x = x - labelWidth / 2;
  }

  // if there is still RIGHT overflow, stick label to right side
  if (x + labelWidth > chartInstance.plotLeft + chartInstance.plotWidth + marginRight) {
    x = chartInstance.plotLeft + chartInstance.plotWidth + marginRight - labelWidth;
  }

  if (point.isHeader) {
    y = chartInstance.chartHeight + chartInstance.plotTop;
  }

  return { x, y };
}

export function updateTrendingChartExtremes(
  event: DateRange<Date>,
  chartInstance: Highcharts.Chart
): void {
  if (!isChartReady(chartInstance)) {
    return;
  }

  const min: number = event.start.valueOf();
  const max: number = event.end.valueOf();

  chartInstance.xAxis[0].setExtremes(min, max);
}

export function isChartReady(chartInstance: Highcharts.Chart): boolean {
  return chartInstance && Object.keys(chartInstance).length > 0;
}

export function showChartTooltip(
  context: any,
  tooltipFormatterContextObject: Highcharts.TooltipFormatterContextObject,
  isShowErrorsContent: boolean,
  isShowFullDateTime: boolean,
  locale?: string,
  format?: string,
  timezone?: string
): string {
  const wrapperStyles = `
      display: flex;
      padding: 8px 16px;
      flex-direction: column;
      gap: 1px;
  `;

  const transparentSeparatorStyles = `
    display: flex;
    width: 100%;
    height: 1px;
    margin: 6px 0;
  `;

  const separatorStyle = `
      display: flex;
      width: 100%;
      background-color: #e7e7e7;
      height: 1px;
      margin: 6px 0;
    `;

  const entryTitleStyles = `
      width: 100%;
      display: flex;
      justify-content: space-between;
    `;

  const entryStyles = `
      width: 100%;
      display: flex;
      justify-content: space-between;
      margin-bottom: 2px;
    `;

  const unitStyles = `
      padding-left: 16px;
      font-weight: bold;
    `;
  let plotLinesToRender = [];

  plotLinesToRender = context?.chart?.xAxis[0]?.plotLinesAndBands?.filter((plotLine) => {
    return !!plotLine?.options?.renderPlotLineInTooltip;
  });

  const plotLinesContent = plotLinesToRender?.map((plotLine) => {
    if (plotLine.options?.type !== CharPlotBandName.Band) {
      const plotLineDate = `<div style="${entryTitleStyles}"><span>${plotLine.options.date}</span></div>`;

      const dateLine = `<div style="${entryStyles}"><span>${plotLineDate}</span></div>`;
      const separator = `<div style="${separatorStyle}"></div>`;
      const transparentSeparator = `<div style="${transparentSeparatorStyles}"> </div>`;

      let errorSection = `${dateLine}${separator}`;

      plotLine.options?.errors?.forEach((error) => {
        const newLine = `<div style="${entryStyles}"><span>${error}</span></div>`;
        errorSection = errorSection + newLine;
      });

      errorSection += transparentSeparator;

      return errorSection;
    }
  });

  const plotBandsToRender = context?.chart?.xAxis[0]?.options?.plotBands
    ?.filter((plotLine) => {
      return plotLine.from < tooltipFormatterContextObject.x;
    })
    ?.at(-1);

  let plotBandText;
  if (plotBandsToRender) {
    const stateValue = `<div>${plotBandsToRender.text.featureName} - ${plotBandsToRender.text.state}</div>`;
    const dateValue = `<div>${plotBandsToRender.text.update} ${moment(
      plotBandsToRender.from
    ).format('HH:mm DD.MM.YYYY')}</div>`;
    const separator = `<div style="${separatorStyle}"></div>`;

    plotBandText = `${stateValue} ${dateValue} ${separator}`;
  }

  const errorsContent = [...plotLinesContent, plotBandText].filter((text) => !!text);

  const trendsContent = tooltipFormatterContextObject.points.reduce(function (
    acc,
    point: any,
    index
  ) {
    const valueLine = `<div style="${entryStyles}"><span><span style="color: ${
      point.series.color
    }; margin-right: 5px">\u25A0</span>${point.series.name}</span><span style="${unitStyles}">${
      Math.round(point.y * 100) / 100
    } ${
      point.series['tooltipOptions'].valueSuffix
        ? point.series['tooltipOptions'].valueSuffix
        : point.yCategory
    }</span></div>`;
    if (index === 0 && isShowFullDateTime) {
      const dateText = (timezone ? dayjs.tz(point.point.x, timezone) : dayjs.utc(point.point.x))
        .locale(locale)
        .format(`HH:mm ${format}`);

      const dateLine = `<div style="${entryTitleStyles}"><span>${dateText}</span></div>`;
      const separator = `<div style="${separatorStyle}"></div>`;

      return `${dateLine}${separator}${valueLine}`;
    }

    return `${acc}${valueLine}`;
  },
  '<b>' + tooltipFormatterContextObject.x + '</b>');

  let wrapper = '';
  if (!!errorsContent && isShowErrorsContent) {
    wrapper = `<span style="${wrapperStyles};">${errorsContent.join(
      ''
    )}</span><span style="${wrapperStyles};">${trendsContent}</span>`;
  } else {
    wrapper = `<span style="${wrapperStyles};">${trendsContent}</span>`;
  }

  return wrapper;
}

export interface TrendingCategory {
  deviceId: string;
  items: TrendingCategoryItem[];
}

export interface TrendingCategoryItem {
  categoryId: string | number;
  categoryName: string;
  features: string[];
  active?: boolean;
  isShow?: boolean;
}

export interface DeviceWithSeries {
  deviceId: string;
  deviceType: string;
  boilerSerial: string;
  shortDeviceName: string;
  data: TrendingSeries[];
}

export interface SeriesToShowByCategory {
  deviceId: string;
  categoryId: string;
  series: TrendingSeries[];
}

export const plotBandFeatures = ['heating.secondaryHeatGenerator.status/value'];
Object.freeze(plotBandFeatures);

// 'false' is possible to get but it's very unlikely and @LblP decided to keep it hidden
// https://viessmann.atlassian.net/browse/DATAENG-5988
export const secondaryHeatGeneratorStatusValue = {
  0: 'standby',
  1: 'heating',
  2: 'on',
  3: 'off',
  4: 'nothing',
  // 5: 'false',
};
Object.freeze(secondaryHeatGeneratorStatusValue);

export const secondaryHeatGeneratorStatusValueColor = {
  0: '#c3d6fc66',
  1: '#d5000066',
  2: '#bfed7266',
  3: '#50f0e666',
  4: '#aa00ff66',
  // 5: '#ffd60066',
};
Object.freeze(secondaryHeatGeneratorStatusValueColor);

export interface PlotBandData {
  from: number;
  to: number;
  color: string;
  text: {
    featureName: string;
    state: string;
    update: string;
  };
  zIndex: number;
  renderPlotLineInTooltip: boolean;
  events: Highcharts.XAxisPlotBandsEventsOptions;
  type: CharPlotBandName;
}

export function plotBandSeriesMapper(
  series: TrendingEnumSeriesData,
  endTime: number
): PlotBandData[] {
  switch (series.feature) {
    case 'heating.secondaryHeatGenerator.status/value':
      return secondaryHeatGeneratorMapper(
        series,
        endTime,
        '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.EXTERNAL_HEAT_GENERATOR'
      );
  }
}

enum CharPlotBandName {
  Band,
}

function secondaryHeatGeneratorMapper(
  input: TrendingEnumSeriesData,
  endTime: number,
  featureName: string
): PlotBandData[] {
  return input.value.map(
    (value, index): PlotBandData => ({
      from: input.start[index],
      color: secondaryHeatGeneratorStatusValueColor[value],
      to: input.start[index + 1] ?? endTime,
      zIndex: 0,
      text: {
        featureName,
        state: `${featureName}_${secondaryHeatGeneratorStatusValue[value].toUpperCase()}`,
        update: '',
      },
      renderPlotLineInTooltip: false,
      events: null,
      type: CharPlotBandName.Band,
    })
  );
}
